/* eslint-disable no-unused-vars */
import React, { cloneElement } from 'react'
import { BrowserRouter, Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { pathToRegexp } from 'path-to-regexp'

import { Box, DialogContent, Dialog as DialogMUI, Grid, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/styles'

import { useLiterals } from '~/components/Literals'
import Cookies from '~/cookie'
import DrawerProduct, { useDrawerProduct } from '~/components/DrawerProduct'
import OnlineStorePage from '~/components/OnlineStorePage'
import useViewportSize from '~/components/useViewportSize'
import AnonymousMode from '~/components/AnonymousMode'

import PositionHandler, { usePositionHandler } from './containers/PositionHandler'
import IndexPage from './containers/IndexPage'
import SearchPage from './containers/SearchPage'
import StoresPage from './containers/StoresPage'
import ShopBasketPage from './containers/ShopBasketPage'
import ProductDetailsPage from './containers/ProductDetailsPage'
import Snack from './containers/Snack'
import Dialog from './containers/Dialog'
import StoreInformations from './containers/StoreInformations'
import StorePage from './containers/StorePage'
import InjectQueryParams from './containers/InjectQueryParams'

import { TYPES } from '~/actions'
import { getDomain } from '~/utils'
import { viewSelector, selectStores } from './selectors'

import Terms from './containers/Terms'
import ringLoadingAI from './assets/ring-loading.svg'

const routersWhiteList = pathToRegexp(
  [
    '/',
    '/termos',
    '/sacola/:gtins',
    '/produto/:gtin/detalhes',
    '/produto/:fakeName/:gtin/detalhes',
    '/loja/:cnpj/produtos',
    '/produto/:fakeName/:gtin',
    '/produto/:gtin',
    '/busca',
    '/onlineStore',
    '/oldOnlineStore',
    '/loja/:cnpj',
    '/produto/:gtin/detalhes',
  ],
  null,
  { sensitive: true },
)

export default function RouterMain({ children, config }) {
  const positionHandler = usePositionHandler(config)
  const view = useSelector(viewSelector)
  const stores = useSelector(selectStores)
  const { drawerProduct, setDrawerProduct, closeDrawerProduct } = useDrawerProduct()

  const commonProps = { position: positionHandler, config }
  const cookie = Cookies.get('session-mode')

  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const { getLiteral } = useLiterals()

  return (
    <BrowserRouter>
      <Snack />
      <InjectQueryParams />
      <Dialog {...commonProps} />

      {positionHandler?.user && Object.values(positionHandler.user).some((i) => i) && (
        <DrawerProduct data={drawerProduct} position={positionHandler} config={config} onClose={closeDrawerProduct} />
      )}

      <DialogMUI open={stores.loading} maxWidth="xs">
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <img src={ringLoadingAI} alt="loading" style={{ width: 100, height: 100 }} />
            <Typography variant="body1" style={{ fontSize: 18, maxWidth: 180, textAlign: 'center', marginBottom: 8 }}>
              {getLiteral('3344')}
            </Typography>
          </Box>
        </DialogContent>
      </DialogMUI>

      <PositionHandler {...positionHandler} config={config} />

      {cookie && view !== TYPES.VIEW_TERM && (
        <Grid
          style={{
            position: 'absolute',
            ...(isMobile ? { bottom: 16, zIndex: 103 } : { top: 16, zIndex: 100 }),
            right: 0,
          }}
          onClick={() => {
            Cookies.set('session-mode', '', getDomain(), 0)
            location.replace('/')
          }}
        >
          <AnonymousMode isMobile={isMobile} />
        </Grid>
      )}

      <Switch>
        <Route path="/termos" exact render={(props) => <Terms language={commonProps.config.language || 'pt'} />} />

        <Route path="/" exact render={(props) => <IndexPage {...props} {...commonProps} />} />

        <Route path="/sacola/:gtins" exact render={(props) => <ShopBasketPage {...props} {...commonProps} />} />

        <Route
          path={['/produto/:gtin/detalhes', '/produto/:fakeName/:gtin/detalhes']}
          exact
          render={(props) => (
            <ProductDetailsPage
              {...props}
              {...commonProps}
              openDrawerProduct={setDrawerProduct}
              closeDrawerProduct={closeDrawerProduct}
            />
          )}
        />

        <Route path={['/loja/:cnpj/produtos']} exact render={(props) => <StorePage {...props} {...commonProps} />} />

        <Route
          path={['/produto/:fakeName/:gtin', '/produto/:gtin']}
          exact
          render={(props) => <StoresPage {...props} {...commonProps} closeDrawerProduct={closeDrawerProduct} />}
        />

        <Route path="/busca" exact render={(props) => <SearchPage {...props} {...commonProps} />} />

        <Route path="/onlineStore" exact render={(props) => <OnlineStorePage {...props} {...commonProps} />} />
      </Switch>

      <Route
        path={['/loja/:cnpj', '/produto/:gtin/detalhes']}
        exact
        render={(props) => {
          return <>{[TYPES.VIEW_STORES].includes(view) && <StoreInformations {...props} {...commonProps} />}</>
        }}
      />

      <Route path="*" component={FallbackRouter} />

      {cloneElement(children, { position: positionHandler, config })}
    </BrowserRouter>
  )
}

function FallbackRouter() {
  const location = useLocation()

  const isRouter = routersWhiteList.test(location.pathname)
  return isRouter ? null : <Redirect to="/" />
}
